import { shortSpinId } from '../../../Model/Spin/spinId.js'
import React, { useMemo, useState } from 'react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import type { RefSpin } from '../../../Generated/RefSpin.ts'
import { Card, KpiTile } from '../AdminStatsPage/StatsUi.js'
import { SpinChrome } from './SpinChrome.js'
import { InfoTip } from './SpinUi.js'
import { shortId, tsLabel } from './spinMetrics.logic.js'
import {
  decodeRefIdParam,
  explanationChecks,
  identityRange,
  identitySource,
  memberTruncationNote,
  num,
  otherRefLabel,
  parseRefKind,
  pct,
  refKindLabel,
  spinTruncationNote
} from './spinRefDetail.logic.js'
import { useSelectedRun } from './useSelectedRun.js'
import { useSpinReferenceDetail } from './useSpinData.js'

/** Cap on the SPIN list we ask for. The backend default is 50; 100 still renders comfortably. */
const SPIN_LIMIT = 100

/**
 * Drill-down for ONE reference id — the page the Reference concordance's "most-split" list dead-ended
 * into. Answers "why is this id split?" by leading with the evidence: the blast radius (KPIs), then the
 * SPINs it landed in, then the member OTUs inside each, then an ordered checklist of the causes that
 * would explain the split.
 *
 * Two hard rules live here: the SPIN/member lists are CAPPED by the backend and the caps are always
 * disclosed (`spinCount` is the true total), and `NCBI_TAXID` is never auto-fetched because its seed
 * column is unindexed (see [useSpinReferenceDetail]).
 */
export const SpinReferenceDetailPage = () => {
  const user = useGetAuthenticatedUser()
  const sel = useSelectedRun()
  const { selected } = sel
  const [params] = useSearchParams()
  const routeParams = useParams<{ refKind: string; refId: string }>()

  const refKind = parseRefKind(routeParams.refKind)
  const refId = decodeRefIdParam(routeParams.refId)
  // Every kind fetches on mount, taxon ids included: V90's expression + partial indexes on
  // raw_blast_results.staxids took the drill-down seed from ~4.4s to ~48ms, and the heaviest composed
  // statement measures ~124ms. (The WHOLE-SET reference list is a different query and still opt-in for
  // taxon ids — it fans staxids out across every current member driven by otu_uuid, which no index on
  // staxids can help. See isSlowWholeSetRefKind.)
  const { detail, loading, error } = useSpinReferenceDetail(selected, refKind, refId, SPIN_LIMIT)

  const qs = params.toString()
  const suffix = qs ? `?${qs}` : ''
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggle = (spinId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(spinId)) next.delete(spinId)
      else next.add(spinId)
      return next
    })

  const spins: RefSpin[] = detail?.spins ?? []
  const checks = useMemo(() => (detail ? explanationChecks(detail) : []), [detail])
  const truncation = detail ? spinTruncationNote(detail) : null

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  const label = refKind ? refKindLabel(refKind) : 'reference id'
  const species = detail?.distinctSpecies ?? []

  return (
    <div className="space-y-8">
      <SpinChrome title="SPIN · Reference detail" sel={sel} />

      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-xl font-semibold">
          {label} <span className="font-mono break-all">{refId || '—'}</span>
          <InfoTip tip="Which current SPINs this one reference id is split across, and the evidence that explains the split. Ids are shown exactly as the backend normalised them: NCBI accessions lowercased, BOLD bins as the extracted key (BOLD:…), never the raw boldsystems.org URL." />
        </h2>
        <Link to={`/admin/spin/reference${suffix}`} className="link link-primary text-sm">
          ← back to reference concordance
        </Link>
      </div>

      {!refKind && (
        <p className="text-error">
          Unknown reference kind &ldquo;{routeParams.refKind}&rdquo; — expected NCBI_ID, NCBI_TAXID or BOLD_BIN.
        </p>
      )}

      {loading && <p className="text-gray-500">Loading reference detail…</p>}
      {error && <p className="text-error">Could not load reference detail: {error}</p>}

      {!loading && !error && detail && detail.spinCount === 0 && (
        <p className="text-gray-500">
          No current member OTU carries this {label.toLowerCase()} as of {tsLabel(detail.asOf)} — nothing to explain.
        </p>
      )}

      {detail && detail.spinCount > 0 && (
        <section className="space-y-4">
          <p className="text-sm text-gray-500">
            Current set of {selected?.algorithmType ?? '—'} as of {tsLabel(detail.asOf)}
            {detail.amplicon ? ` · amplicon ${detail.amplicon}` : ' · all amplicons'}.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiTile
              label={
                <>
                  OTUs carrying the id
                  <InfoTip tip="Current member OTUs (latest-wins by assigned_at) whose blast row produced this reference id. The blast radius of the split — counted over ALL of them, not only the SPINs listed below." />
                </>
              }
              value={num(detail.otuCount)}
            />
            <KpiTile
              label={
                <>
                  Distinct SPINs
                  <InfoTip tip="How many different current SPINs those OTUs fall into. 1 = SPIN kept the id together; >1 = it split it. This is the TRUE total even when the table below is capped." />
                </>
              }
              value={num(detail.spinCount)}
            />
            <KpiTile
              label={
                <>
                  {identitySource(detail.refKind)} identity range
                  <InfoTip tip="Min–max hit percentage from the blast row that produced the id — the NCBI row for the NCBI kinds, the BOLD row for BOLD_BIN — across all carrying OTUs. High-and-narrow means SPIN split despite near-perfect matches; low means the reference is promiscuous and splitting is correct. Empty when there is no readable blast percentage." />
                </>
              }
              value={identityRange(detail.hitPercentageMin, detail.hitPercentageMax)}
            />
            <KpiTile
              label={
                <>
                  Distinct resolved species
                  <InfoTip tip="Consensus species across ALL current SPINs carrying the id (not only the listed ones), capped by the backend. One species across many SPINs is the strongest 'should have been one cluster' signal." />
                </>
              }
              // The backend caps this list, so on a wide id the count is a floor, not an exact figure —
              // `speciesTruncated` is the only way to tell, and an uncapped true total is never computed.
              value={detail.speciesTruncated ? `≥ ${num(species.length)}` : num(species.length)}
            />
          </div>

          {species.length > 0 && (
            <p className="text-sm text-gray-500">
              Resolved species: <span className="font-mono">{species.join(' · ')}</span>
              {detail.speciesTruncated && (
                <span className="text-warning">
                  {' '}
                  — capped at {species.length.toLocaleString()}; there are more, and the true total is not computed.
                </span>
              )}
            </p>
          )}

          <Card title={`SPINs carrying this ${label.toLowerCase()} (${spins.length.toLocaleString()})`}>
            {truncation && (
              <p className="text-sm text-warning font-medium mb-2">
                {truncation}
                <InfoTip tip="The backend caps the SPIN list (ordered by members-with-this-id descending). The KPI above reports the true total — on real data the worst accession spans 2,397 SPINs across 6,268 OTUs." />
              </p>
            )}
            {spins.length === 0 ? (
              <p className="text-gray-500 text-sm">The response returned no SPIN rows.</p>
            ) : (
              <div className="overflow-x-auto max-h-[40rem]">
                <table className="table table-xs table-pin-rows">
                  <thead>
                    <tr>
                      <th />
                      <th>SPIN</th>
                      <th>Amplicon</th>
                      <th className="text-right">
                        With this id
                        <InfoTip tip="Current members of this SPIN that carry the drilled-down reference id." />
                      </th>
                      <th className="text-right">
                        Members
                        <InfoTip tip="Total current members of the SPIN (latest-wins). 1 = a singleton: a fragment holding only this OTU, not a competing cluster." />
                      </th>
                      <th className="text-right">
                        Distinct ids
                        <InfoTip tip="Purity — how many different reference ids of this kind the SPIN lumps together. 1 = pure." />
                      </th>
                      <th>Genus</th>
                      <th>Species</th>
                      <th className="text-right">
                        Core len
                        <InfoTip tip="Length of the SPIN's core sequence in bp (length only — trigram similarity cannot separate same-species from different-phylum on DNA). Differing lengths mean these are different sequences." />
                      </th>
                      <th>Minted at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spins.map((s) => {
                      const isOpen = expanded.has(s.spinId)
                      const memberNote = memberTruncationNote(s)
                      const members = s.members ?? []
                      return (
                        <React.Fragment key={s.spinId}>
                          <tr className={s.currentMemberCount === 1 ? 'bg-warning/10' : undefined}>
                            <td>
                              <button
                                className="btn btn-ghost btn-xs"
                                onClick={() => toggle(s.spinId)}
                                title={isOpen ? 'Hide members' : 'Show members'}
                                aria-expanded={isOpen}
                              >
                                {isOpen ? '▾' : '▸'}
                              </button>
                            </td>
                            <td className="font-mono">
                              <Link to={`/admin/spin/s/${s.spinId}${suffix}`} className="link link-primary">
                                {shortSpinId(s.spinId)}
                              </Link>
                            </td>
                            <td>{s.amplicon ?? '—'}</td>
                            <td className="text-right tabular-nums">{num(s.membersWithRef)}</td>
                            <td className="text-right tabular-nums">
                              {s.currentMemberCount === 1 ? (
                                <span className="badge badge-warning badge-xs" title="Singleton — this SPIN holds only one current member">
                                  1
                                </span>
                              ) : (
                                num(s.currentMemberCount)
                              )}
                            </td>
                            <td className="text-right tabular-nums">{num(s.distinctRefIds)}</td>
                            <td>{s.genus ?? '—'}</td>
                            <td>{s.species ?? '—'}</td>
                            <td className="text-right tabular-nums">{s.coreSequenceLength == null ? '—' : num(s.coreSequenceLength)}</td>
                            <td className="whitespace-nowrap">{tsLabel(s.mintedAt)}</td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td />
                              <td colSpan={9} className="bg-base-200/50">
                                {memberNote && <p className="text-xs text-warning font-medium mb-1">{memberNote}</p>}
                                {(s.predecessorSpinIds ?? []).length > 0 && (
                                  <p className="text-xs text-gray-500 mb-1">
                                    Predecessors:{' '}
                                    {(s.predecessorSpinIds ?? []).map((p, i) => (
                                      <React.Fragment key={p}>
                                        {i > 0 && ' · '}
                                        <Link to={`/admin/spin/s/${p}${suffix}`} className="link font-mono">
                                          {shortSpinId(p)}
                                        </Link>
                                      </React.Fragment>
                                    ))}
                                  </p>
                                )}
                                {members.length === 0 ? (
                                  <p className="text-xs text-gray-500">No member rows returned for this SPIN.</p>
                                ) : (
                                  <table className="table table-xs">
                                    <thead>
                                      <tr>
                                        <th>OTU</th>
                                        <th>Rep</th>
                                        <th className="text-right">Reads</th>
                                        <th className="text-right">
                                          {identitySource(detail.refKind)} identity
                                          <InfoTip tip="hit_percentage from the blast row that produced THIS id (the NCBI row for the NCBI kinds, the BOLD row for BOLD_BIN) — not a generic best hit. A member whose id came from the other source legitimately has none." />
                                        </th>
                                        <th className="text-right">{identitySource(detail.refKind)} coverage</th>
                                        <th className="text-right">{identitySource(detail.refKind)} grade</th>
                                        <th>
                                          {otherRefLabel(detail.refKind)}
                                          <InfoTip tip="The CROSS-source identifier for the same OTU. If the other source agrees where SPIN disagrees, that is a strong hint." />
                                        </th>
                                        <th>
                                          Assigned at
                                          <InfoTip tip="From the latest-wins membership row. Members assigned at very different times may simply never have been re-clustered together." />
                                        </th>
                                        <th>Assigned run</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {members.map((m) => (
                                        <tr key={m.otuUuid}>
                                          <td className="font-mono">{shortId(m.otuUuid)}</td>
                                          <td>{m.isRepresentative ? '★' : ''}</td>
                                          <td className="text-right tabular-nums">{num(m.reads)}</td>
                                          <td className="text-right tabular-nums">{pct(m.hitPercentage)}</td>
                                          <td className="text-right tabular-nums">
                                            {m.queryCoverage == null ? '—' : `${m.queryCoverage}%`}
                                          </td>
                                          <td className="text-right tabular-nums">{pct(m.gradePercentage)}</td>
                                          <td className="font-mono break-all">{m.otherRefId ?? '—'}</td>
                                          <td className="whitespace-nowrap">{tsLabel(m.assignedAt)}</td>
                                          <td className="font-mono">{m.assignedRunId ? shortId(m.assignedRunId) : '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="What to check, in order">
            <p className="text-sm text-gray-600 mb-3">
              Work down this list and stop at the first cause that fires — the earlier entries explain a split outright, the later ones
              only hint. Highlighted rows are the ones this id&apos;s data actually shows.
            </p>
            <ol className="space-y-2 list-decimal list-inside">
              {checks.map((c) => (
                <li key={c.key} className={c.flagged ? 'text-gray-800' : 'text-gray-400'}>
                  <span className="font-semibold">{c.label}</span>
                  {c.flagged && <span className="badge badge-warning badge-xs ml-2 align-middle">{c.evidence}</span>}
                  {!c.flagged && <span className="text-xs ml-2">not indicated</span>}
                  <p className="text-xs ml-6 mt-0.5">{c.detail}</p>
                </li>
              ))}
            </ol>
            <p className="text-xs text-gray-500 mt-3">
              Anything computed from the table above (amplicons, core lengths, singletons, timestamps, predecessors) only sees the SPINs
              actually returned — with a capped list, treat those signals as a lower bound.
            </p>
          </Card>
        </section>
      )}
    </div>
  )
}
